// src/Pages/PaymentResult.jsx
import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * ✅ Navigate example:
 * navigate("/payment-result", {
 *   state: {
 *     status: "SUCCESS", // or "FAILED"
 *     method: "RAZORPAY", // "RAZORPAY" | "WALLET" | "COD"
 *     orderId: 32,
 *     amount: 31,
 *     currency: "INR",
 *     createdAt: new Date().toISOString(),
 *     // OPTIONAL:
 *     customerName: "Tambi",
 *     customerPhone: "9000000000",
 *     razorpay_order_id: "order_xxx",
 *     razorpay_payment_id: "pay_xxx",
 *     razorpay_signature: "sig_xxx",
 *     walletTxnId: "WALLET_TXN_123",
 *     walletBalance: 1234.5,
 *     message: "Payment successful"
 *   }
 * });
 */

const safe = (v, fb = "-") =>
  v === null || v === undefined || String(v).trim() === "" ? fb : v;

const money = (v) => {
  const n = Number(v ?? 0);
  if (!Number.isFinite(n)) return "0";
  return n.toFixed(0);
};

const fmtDateTime = (dt) => {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "—";
  }
};

const isSuccessStatus = (status) => {
  const s = String(status || "").toUpperCase();
  return s === "SUCCESS" || s === "PAID";
};

function formatMethod(m) {
  const x = String(m || "").toUpperCase();
  if (x === "RAZORPAY") return "Razorpay";
  if (x === "WALLET") return "Wallet";
  if (x === "COD") return "Cash On Delivery";
  return safe(m, "-");
}

function maskId(id, keepStart = 6, keepEnd = 4) {
  const s = String(id || "");
  if (!s) return "-";
  if (s.length <= keepStart + keepEnd) return s;
  return `${s.slice(0, keepStart)}••••••${s.slice(-keepEnd)}`;
}

function InfoRow({ label, value }) {
  return (
    <div style={S.row}>
      <div style={S.rowLabel}>{label}</div>
      <div style={S.rowValue}>{safe(value)}</div>
    </div>
  );
}

export default function PaymentResult() {
  const navigate = useNavigate();
  const { state } = useLocation();

  // fallback if refresh
  const status = state?.status || "SUCCESS";
  const ok = useMemo(() => isSuccessStatus(status), [status]);

  const method = state?.method || state?.paymentMethod || "RAZORPAY";
  const amount = state?.amount ?? state?.grandTotal ?? 0;
  const currency = state?.currency || "INR";
  const createdAt = state?.createdAt || new Date().toISOString();

  const orderId = state?.orderId || state?.internal_order_id || "";
  const message = state?.message || "";

  // optional customer fields (NO USER ID per your request)
  const customerName = state?.customerName || state?.name || "";
  const customerPhone = state?.customerPhone || state?.phone || "";

  // Razorpay fields
  const razorpay_order_id = state?.razorpay_order_id || state?.razorpayOrderId || "";
  const razorpay_payment_id = state?.razorpay_payment_id || state?.razorpayPaymentId || "";
  const razorpay_signature = state?.razorpay_signature || state?.razorpaySignature || "";

  // Wallet fields
  const walletTxnId = state?.walletTxnId || state?.transactionId || "";
  const walletBalance = state?.walletBalance;

  const goOrders = () => navigate("/Order");
  const goHome = () => navigate("/Dashboard");
  const goCart = () => navigate("/Cart");

  const hasAny =
    state &&
    (orderId ||
      String(amount || "") ||
      razorpay_payment_id ||
      razorpay_order_id ||
      walletTxnId ||
      message ||
      customerName ||
      customerPhone);

  if (!hasAny) {
    return (
      <>
        <style>{baseCss}</style>
        <div style={S.page}>
          <div style={S.wrap}>
            <div style={S.hero(false)}>
              <div style={S.tickRing(false)}>⚠️</div>
              <div style={S.heroTitle}>Session Expired</div>
              <div style={S.heroSub}>Please open this page from Cart again.</div>
            </div>

            <div style={S.card}>
              <button style={S.btnDark} onClick={goCart}>
                Go to Cart
              </button>
              <button style={{ ...S.btnOutline, marginTop: 10 }} onClick={goHome}>
                Go Home
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{baseCss}</style>

      <div style={S.page}>
        <div style={S.wrap}>
          {/* ✅ Top header */}
          <div style={S.hero(ok)}>
            <div style={S.tickRing(ok)}>
              {ok ? (
                <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M20 6L9 17l-5-5"
                    stroke="#2F7D32"
                    strokeWidth="2.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="#b93434"
                    strokeWidth="2.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>

            <div style={S.heroTitle}>{ok ? "Payment Successful" : "Payment Failed"}</div>
            <div style={S.heroSub}>{fmtDateTime(createdAt)}</div>
          </div>

          {/* ✅ White card */}
          <div style={S.card}>

            {message ? <div style={S.message(ok)}>{message}</div> : null}

            {/* ✅ Details list (NO COPY buttons, NO bold) */}
            <div style={S.sectionTitle}>Transaction Details</div>

            <div style={S.box}>
              <InfoRow label="Status" value={String(status || "").toUpperCase()} />
              <InfoRow label="Payment Method" value={formatMethod(method)} />
              <InfoRow label="Order ID" value={orderId || "-"} />
              <InfoRow
                label="Amount"
                value={`${currency === "INR" ? "₹" : `${currency} `}${money(amount)}`}
              />

              {String(method).toUpperCase() === "RAZORPAY" ? (
                <>
                  <InfoRow
                    label="Razorpay Order ID"
                    value={razorpay_order_id ? maskId(razorpay_order_id) : "-"}
                  />
                  <InfoRow
                    label="Razorpay Payment ID"
                    value={razorpay_payment_id ? maskId(razorpay_payment_id) : "-"}
                  />
                  <InfoRow
                    label="Razorpay Signature"
                    value={razorpay_signature ? maskId(razorpay_signature, 8, 6) : "-"}
                  />
                </>
              ) : null}

              {String(method).toUpperCase() === "WALLET" ? (
                <>
                  <InfoRow
                    label="Wallet Transaction ID"
                    value={walletTxnId ? maskId(walletTxnId) : "-"}
                  />
                  <InfoRow
                    label="Wallet Balance"
                    value={walletBalance != null ? `₹ ${Number(walletBalance).toFixed(2)}` : "-"}
                  />
                </>
              ) : null}
            </div>

            {/* ✅ Actions */}
            <div style={S.actions}>
              <button style={S.btnOutline} onClick={goCart}>
                ← Back to Cart
              </button>
              <button style={S.btnSoft} onClick={goOrders}>
                View Orders
              </button>
              <button style={S.btnGreen} onClick={goHome}>
                Done
              </button>
            </div>

            <div style={S.footerNote}>
              {ok
                ? "Order placed successfully. You can track it in Orders page."
                : "If money is deducted, it may auto-refund. You can retry payment from Cart."}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const baseCss = `
  *{ box-sizing:border-box; margin:0; padding:0; }
  html, body, #root{
    height:100%;
    width:100%;
    font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial;
    background:#f6f7fb;
    color:#111;
  }
`;

const S = {
  page: {
    minHeight: "100vh",
    background:
      "radial-gradient(900px 600px at 15% 10%, rgba(0,0,0,.04), transparent 55%), radial-gradient(900px 600px at 90% 20%, rgba(0,0,0,.03), transparent 55%), linear-gradient(180deg, #ffffff, #f6f7fb)",
    padding: "16px 12px 28px",
    display: "grid",
    placeItems: "start center",
  },
  wrap: { width: "100%", maxWidth: 520, display: "grid", gap: 14 },

  hero: (ok) => ({
    borderRadius: 18,
    padding: "22px 16px",
    color: "#fff",
    background: ok
      ? "linear-gradient(180deg, #2F7D32, #1e5e26)"
      : "linear-gradient(180deg, #d64545, #b93434)",
    boxShadow: "0 18px 50px rgba(0,0,0,.12)",
    textAlign: "center",
  }),
  tickRing: (ok) => ({
    width: 78,
    height: 78,
    margin: "0 auto 10px",
    borderRadius: 999,
    background: "rgba(255,255,255,.92)",
    display: "grid",
    placeItems: "center",
    boxShadow: "0 10px 24px rgba(0,0,0,.14)",
    outline: ok ? "10px solid rgba(255,255,255,.18)" : "10px solid rgba(255,255,255,.12)",
    fontSize: 26,
  }),
  heroTitle: { fontSize: 22, fontWeight: 800, letterSpacing: 0.2 },
  heroSub: { marginTop: 8, fontSize: 13, opacity: 0.92, fontWeight: 600 },

  card: {
    background: "#fff",
    border: "1px solid rgba(0,0,0,.10)",
    borderRadius: 18,
    padding: 14,
    boxShadow: "0 18px 50px rgba(0,0,0,.08)",
  },

  // top strip
  topStrip: {
    borderRadius: 16,
    border: "1px solid rgba(0,0,0,.10)",
    background: "rgba(0,0,0,.02)",
    padding: 12,
    display: "grid",
    gridTemplateColumns: "1fr 140px",
    alignItems: "center",
    gap: 12,
  },
  leftMini: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
  avatar: {
    width: 42,
    height: 42,
    borderRadius: 14,
    background: "rgba(0,0,0,.05)",
    border: "1px solid rgba(0,0,0,.10)",
    display: "grid",
    placeItems: "center",
    flex: "0 0 auto",
  },
  nameMini: {
    fontSize: 14,
    fontWeight: 650,
    color: "#111",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  mobileMini: { marginTop: 4, fontSize: 12, color: "rgba(0,0,0,.65)", fontWeight: 500 },

  // amount center
  amountCenter: {
    textAlign: "center",
    borderLeft: "1px solid rgba(0,0,0,.08)",
    paddingLeft: 12,
  },
  amountBig: { fontSize: 24, fontWeight: 700, color: "#111", lineHeight: 1.1 },
  amountCaption: { marginTop: 4, fontSize: 12, color: "rgba(0,0,0,.55)" },

  message: (ok) => ({
    marginTop: 12,
    padding: "10px 12px",
    borderRadius: 14,
    border: ok ? "1px solid rgba(47,125,50,.22)" : "1px solid rgba(185,52,52,.22)",
    background: ok ? "rgba(47,125,50,.06)" : "rgba(185,52,52,.06)",
    color: "#111",
    fontSize: 13,
    fontWeight: 600,
  }),

  sectionTitle: { marginTop: 14, fontSize: 13, fontWeight: 650, color: "rgba(0,0,0,.80)" },

  box: {
    marginTop: 10,
    borderRadius: 16,
    border: "1px solid rgba(0,0,0,.10)",
    background: "#fff",
    overflow: "hidden",
  },

  row: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
    alignItems: "center",
    padding: "12px 12px",
    borderBottom: "1px dashed rgba(0,0,0,.12)",
  },
  rowLabel: { fontSize: 12.5, color: "rgba(0,0,0,.70)", fontWeight: 550 },
  rowValue: {
    fontSize: 12.5,
    color: "#111",
    fontWeight: 550,
    textAlign: "right",
    wordBreak: "break-word",
  },

  actions: {
    marginTop: 14,
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
  },

  btnOutline: {
    width: "100%",
    padding: "12px 12px",
    borderRadius: 14,
    border: "1px solid rgba(0,0,0,.16)",
    background: "#fff",
    color: "#111",
    fontWeight: 700,
    cursor: "pointer",
  },
  btnSoft: {
    width: "100%",
    padding: "12px 12px",
    borderRadius: 14,
    border: "1px solid rgba(47,125,50,.20)",
    background: "rgba(47,125,50,.06)",
    color: "#2F7D32",
    fontWeight: 700,
    cursor: "pointer",
  },
  btnGreen: {
    width: "100%",
    padding: "12px 12px",
    borderRadius: 14,
    border: "1px solid rgba(47,125,50,.35)",
    background: "linear-gradient(180deg, #2F7D32, #1e5e26)",
    color: "#fff",
    fontWeight: 750,
    cursor: "pointer",
    gridColumn: "1 / -1",
  },

  footerNote: {
    marginTop: 10,
    fontSize: 12.5,
    color: "rgba(0,0,0,.65)",
    textAlign: "center",
    fontWeight: 600,
  },

  // fallback buttons
  btnDark: {
    width: "100%",
    padding: "12px 12px",
    borderRadius: 14,
    border: "1px solid rgba(0,0,0,.14)",
    background: "#111",
    color: "#fff",
    fontWeight: 750,
    cursor: "pointer",
  },
};